import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useI18n } from '../../i18n'
import {
  listConfigs,
  getActiveConfig,
  createConfig,
  cloneConfig,
  activateConfig,
  deleteConfig,
  updateConfig,
} from '../../api/graphConfigs'
import { getToken, API_PATH_ADMIN } from '../../api/client'
import { useToast } from '../../contexts/ToastContext'
import CreateConfigModal from '../../components/CreateConfigModal'
import ConfirmModal from '../../components/ConfirmModal'
import EntityCard from '../../components/EntityCard'
import { IconEdit, IconCopy, IconCheck, IconTrash, IconRename } from '../../components/ui/Icons'
import { PageSection } from '../../components/ui'
import '../../styles/pages/settings.css'
import '../../styles/pages/page-common.css'

export default function SettingsConfiguration() {
  const { t } = useI18n()
  const { pathname } = useLocation()
  const { showToast } = useToast()
  const basePath = pathname.startsWith('/v2') ? '/v2/configuration' : '/old/settings/configuration'
  const [configs, setConfigs] = useState([])
  const [activeId, setActiveId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [activateTarget, setActivateTarget] = useState(null)
  const [activateErrors, setActivateErrors] = useState([])
  const [renameTarget, setRenameTarget] = useState(null) // { id, name }

  async function load() {
    setLoading(true)
    try {
      const [list, active] = await Promise.all([listConfigs(), getActiveConfig()])
      setConfigs(list)
      setActiveId(active?.id ?? null)
    } catch (e) {
      // Toast показывается в api/client
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  async function handleCreate(name) {
    try {
      await createConfig(name)
      showToast(t('settings.config.created') || 'Конфиг создан', 'success')
      setCreateModalOpen(false)
      load()
    } catch (e) {
      // Toast показывается в api/client
    }
  }

  async function handleClone(id) {
    try {
      await cloneConfig(id)
      showToast(t('settings.config.cloned') || 'Конфиг склонирован', 'success')
      load()
    } catch (e) {
      // Toast показывается в api/client
    }
  }

  async function handleActivateClick(id) {
    setActivateTarget(id)
    setActivateErrors([])
    try {
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || '/api'}${API_PATH_ADMIN}/graph-configs/${id}/validate`, {
        headers: { 'Authorization': `Bearer ${getToken()}` },
        cache: 'no-store',
      })
      const data = await res.json()
      if (data.errors?.length) setActivateErrors(data.errors)
    } catch { /* ignore */ }
  }

  async function handleActivateConfirm() {
    if (!activateTarget) return
    try {
      await activateConfig(activateTarget)
      setActiveId(activateTarget)
      showToast(t('settings.config.activated') || 'Конфиг активирован', 'success')
      setActivateTarget(null)
      setActivateErrors([])
      load()
    } catch (e) {
      // Toast показывается в api/client
    }
  }

  async function handleDeleteConfirm() {
    if (!deleteTarget) return
    try {
      await deleteConfig(deleteTarget)
      showToast(t('settings.config.deleted') || 'Конфиг удалён', 'success')
      setDeleteTarget(null)
      load()
    } catch (e) {
      // Toast показывается в api/client
    }
  }

  async function handleRename(newName) {
    if (!renameTarget) return
    try {
      await updateConfig(renameTarget.id, { name: newName })
      showToast(t('settings.config.renamed') || 'Конфиг переименован', 'success')
      setRenameTarget(null)
      load()
    } catch (e) {
      // Toast показывается в api/client
    }
  }

  if (loading) {
    return <p className="settings-loading">{t('settings.loading') || 'Загрузка…'}</p>
  }

  return (
    <>
    <PageSection
      className="settings-configuration"
      title={t('layout.nav.configuration')}
      actions={
        <button type="button" className="btn btn-primary" onClick={() => setCreateModalOpen(true)}>
          {t('settings.config.create') || 'Создать'}
        </button>
      }
    >
      <div className="settings-config-cards">
        {configs.length === 0 ? (
          <p className="settings-config-empty">{t('settings.config.empty') || 'Нет конфигов. Создайте конфигурацию.'}</p>
        ) : (
          configs.map((c) => (
            <EntityCard
              key={c.id}
              title={c.name}
              titleHref={`${basePath}/${c.id}`}
              badge={c.is_active ? (t('settings.config.active') || 'Активный') : null}
              active={c.is_active}
              actions={[
                { icon: <IconEdit />, title: t('settings.config.edit') || 'Редактировать', href: `${basePath}/${c.id}` },
                { icon: <IconRename />, title: t('settings.config.rename') || 'Переименовать', onClick: () => setRenameTarget({ id: c.id, name: c.name }) },
                ...(!c.is_active ? [{ icon: <IconCheck />, title: t('settings.config.activate') || 'Активировать', onClick: () => handleActivateClick(c.id), variant: 'accent' }] : []),
                { icon: <IconCopy />, title: t('settings.config.clone') || 'Клонировать', onClick: () => handleClone(c.id) },
                ...(!c.is_active ? [{ icon: <IconTrash />, title: t('settings.config.delete') || 'Удалить', onClick: () => setDeleteTarget(c.id), variant: 'danger' }] : []),
              ]}
            />
          ))
        )}
      </div>
    </PageSection>
      <CreateConfigModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreate}
      />
      <ConfirmModal
        open={!!activateTarget}
        title={activateErrors.length ? (t('settings.config.activateBlocked') || 'Конфигурация не готова к активации') : (t('settings.config.activateConfirmTitle') || 'Активировать конфигурацию?')}
        message={activateErrors.length ? activateErrors.map((e, i) => `• ${e}`).join('\n') : t('settings.config.activateConfirmMessage')}
        confirmLabel={activateErrors.length ? null : (t('settings.config.activate') || 'Активировать')}
        cancelLabel={t('common.cancel') || 'Закрыть'}
        onConfirm={handleActivateConfirm}
        onCancel={() => { setActivateTarget(null); setActivateErrors([]) }}
      />
      <ConfirmModal
        open={!!deleteTarget}
        title={t('settings.config.deleteConfirm') || 'Удалить конфиг?'}
        confirmLabel={t('settings.config.delete') || 'Удалить'}
        cancelLabel={t('common.cancel') || 'Отмена'}
        danger
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
      <CreateConfigModal
        open={!!renameTarget}
        onClose={() => setRenameTarget(null)}
        onCreate={handleRename}
        initialName={renameTarget?.name || ''}
        title={t('settings.config.renameModalTitle') || 'Переименовать конфигурацию'}
        submitLabel={t('settings.config.rename') || 'Переименовать'}
      />
    </>
  )
}
